import * as React from 'react'

import { Toggle as TogglePrimitive } from '@base-ui/react/toggle'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

const toggleVariants = cva(
  'tw:group/toggle tw:inline-flex tw:items-center tw:justify-center tw:gap-1 tw:rounded-md tw:text-sm tw:font-medium tw:whitespace-nowrap tw:transition-all tw:outline-none tw:hover:bg-muted tw:hover:text-foreground tw:focus-visible:border-ring tw:focus-visible:ring-[3px] tw:focus-visible:ring-ring/50 tw:disabled:pointer-events-none tw:disabled:opacity-50 tw:aria-invalid:border-destructive tw:aria-invalid:ring-destructive/20 tw:aria-pressed:bg-muted tw:data-[state=on]:bg-muted tw:dark:aria-invalid:ring-destructive/40 tw:[&_svg]:pointer-events-none tw:[&_svg]:shrink-0 tw:[&_svg:not([class*=size-])]:size-4',
  {
    variants: {
      variant: {
        default: 'tw:bg-transparent',
        outline:
          'tw:border tw:border-input tw:bg-transparent tw:hover:bg-muted',
        success:
          'tw:border tw:border-success-soft tw:bg-transparent tw:text-success-soft-foreground tw:hover:border-success-soft-hover tw:hover:bg-success-soft tw:hover:text-success-soft-foreground tw:aria-pressed:border-success tw:aria-pressed:bg-success-soft tw:aria-pressed:text-success-soft-foreground',
        destructive:
          'tw:border tw:border-destructive-soft tw:bg-transparent tw:text-destructive-soft-foreground tw:hover:border-destructive-soft-hover tw:hover:bg-destructive-soft tw:hover:text-destructive-soft-foreground tw:aria-pressed:border-destructive tw:aria-pressed:bg-destructive-soft tw:aria-pressed:text-destructive-soft-foreground',
        neutral:
          'tw:border tw:border-input-soft tw:bg-transparent tw:text-muted-foreground tw:hover:bg-accent-soft tw:hover:text-foreground tw:aria-pressed:border-muted-foreground tw:aria-pressed:bg-muted tw:aria-pressed:text-foreground',
      },
      size: {
        default: 'tw:h-8 tw:min-w-8 tw:px-2',
        sm: 'tw:h-7 tw:min-w-7 tw:rounded-[min(var(--radius-md),12px)] tw:px-2.5 tw:text-[0.8rem] tw:has-data-[icon=inline-end]:pr-1.5 tw:has-data-[icon=inline-start]:pl-1.5 tw:[&_svg:not([class*=size-])]:size-3.5',
        lg: 'tw:h-9 tw:min-w-9 tw:px-2.5 tw:has-data-[icon=inline-end]:pr-2 tw:has-data-[icon=inline-start]:pl-2',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Toggle({
  className,
  variant = 'default',
  size = 'default',
  readOnly = false,
  ...props
}: TogglePrimitive.Props &
  VariantProps<typeof toggleVariants> & { readOnly?: boolean }) {
  return (
    <TogglePrimitive
      data-slot="toggle"
      className={cn(
        toggleVariants({ variant, size }),
        readOnly && 'tw:pointer-events-none',
        className,
      )}
      {...props}
      {...(readOnly ? { tabIndex: -1, 'aria-readonly': true } : {})}
    />
  )
}

export { Toggle, toggleVariants }
